import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useBlocker, useNavigate } from 'react-router-dom'
import { api, ApiError } from '../../lib/api'
import { comicPath, today } from '../../lib/format'
import { ArtistComic, DisplayStyle } from '../../lib/types'
import { useImageUploads } from '../../lib/useImageUploads'
import { Caption, ComicImages, MetaLine } from '../comic'
import { Dialog } from '../Dialog'
import { Icon } from '../Icon'
import { Alert, EmptyState } from '../states'
import { CaptionField, DisplayStyleField, TagField } from './fields'
import { ImageList } from './ImageList'
import { SOCIAL_TITLE, SocialDialog } from './SocialDialog'

type Status = 'published' | 'draft'

interface Problems {
  summary: string
  fields: Record<string, string>
}

function PublishedScreen({ comic, onAnother }: { comic: ArtistComic; onAnother: () => void }) {
  const [copied, setCopied] = useState(false)
  const [social, setSocial] = useState(false)
  const url = `${window.location.origin}${comicPath(comic.slug)}`
  return (
    <>
    <EmptyState
      icon="check"
      title="Your comic is live"
      actions={
        <>
          <div className="btns">
            <Link className="btn solid" to={comicPath(comic.slug)}>
              View comic
            </Link>
            <button
              type="button"
              className="btn"
              onClick={() =>
                navigator.clipboard
                  .writeText(url)
                  .then(() => setCopied(true))
                  .catch(() => window.prompt('Copy this link:', url))
              }
            >
              {copied ? 'Link copied' : 'Copy link'}
            </button>
            <button type="button" className="btn" onClick={() => setSocial(true)}>
              {SOCIAL_TITLE}
            </button>
            <button type="button" className="btn" onClick={onAnother}>
              Upload another
            </button>
          </div>
          <Link to="/artist/comics">
            Go to Manage comics
          </Link>
        </>
      }
    >
      {comic.title} is published and visible to everyone.
    </EmptyState>
    {social && <SocialDialog comic={comic} onClose={() => setSocial(false)} />}
    </>
  )
}

/** Upload a new comic, or edit one (initial set). Drafts and published comics both use it. */
export function ComicForm({ initial, initialMessage }: { initial?: ArtistComic; initialMessage?: string }) {
  const navigate = useNavigate()
  const [comic, setComic] = useState<ArtistComic | undefined>(initial)
  const editing = Boolean(comic)
  const status: Status = comic?.status ?? 'draft'

  const uploads = useImageUploads(initial?.images ?? [])
  const [title, setTitle] = useState(initial?.title ?? '')
  const [caption, setCaption] = useState(initial?.caption ?? '')
  const [tags, setTags] = useState<string[]>(initial?.tags ?? [])
  const [date, setDate] = useState(initial ? initial.happenedOnDate : today())
  const [displayStyle, setDisplayStyle] = useState<DisplayStyle>(initial?.displayStyle ?? 'auto')
  const [thumbId, setThumbId] = useState<string | undefined>(() => uploads.items[initial?.thumbnailIndex ?? 0]?.id)

  const [busy, setBusy] = useState<null | 'saving' | 'publishing' | 'deleting'>(null)
  const [message, setMessage] = useState(initialMessage ?? '')
  const [problems, setProblems] = useState<Problems | null>(null)
  const [saveFailed, setSaveFailed] = useState<null | { title: string; retry: () => void }>(null)
  const [preview, setPreview] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [published, setPublished] = useState<ArtistComic | null>(null)

  // The thumbnail stays on its image when images move; falls back to the first
  const savedItems = uploads.items.filter((it) => it.key)
  const thumbIndex = Math.max(0, savedItems.findIndex((it) => it.id === thumbId))
  useEffect(() => {
    if (!uploads.items.some((it) => it.id === thumbId)) setThumbId(uploads.items[0]?.id)
  }, [uploads.items, thumbId])

  const values = useMemo(
    () => ({
      title: title.trim(),
      caption: caption.trim(),
      tags,
      happenedOnDate: date,
      displayStyle,
      images: uploads.saved,
      thumbnailIndex: thumbIndex,
    }),
    [title, caption, tags, date, displayStyle, uploads.saved, thumbIndex]
  )
  const snapshot = JSON.stringify({ ...values, pending: uploads.items.length - savedItems.length })
  const [savedSnapshot, setSavedSnapshot] = useState(snapshot)
  const dirty = snapshot !== savedSnapshot && !published
  const leaving = useRef(false)

  // Warn before leaving with unsaved changes: in the site, and when closing the tab
  const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty && !leaving.current && currentLocation.pathname !== nextLocation.pathname)
  useEffect(() => {
    if (!dirty) return
    const warn = (e: BeforeUnloadEvent) => e.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  const check = (next: Status): Problems | null => {
    if (next !== 'published') return null
    const fields: Record<string, string> = {}
    if (uploads.saved.length === 0) fields.images = 'Add at least one image.'
    if (!values.title) fields.title = 'Add a title before publishing.'
    const n = Object.keys(fields).length
    if (!n) return null
    const summary = n === 1 ? '1 thing needs fixing before you can publish' : `${n} things need fixing before you can publish`
    return { summary, fields }
  }

  const save = useCallback(
    async (next: Status, then?: () => void): Promise<boolean> => {
      setMessage('')
      setSaveFailed(null)
      const found = check(next)
      setProblems(found)
      setPreview(false)
      if (found) {
        window.scrollTo({ top: 0, behavior: 'smooth' })
        return false
      }
      setBusy(next === 'published' && status === 'draft' ? 'publishing' : 'saving')
      try {
        const body = { ...values, status: next }
        const res = comic
          ? await api.put<{ comic: ArtistComic }>(`/api/admin/comics/${comic.id}`, body)
          : await api.post<{ comic: ArtistComic }>('/api/admin/comics', body)
        setSavedSnapshot(snapshot)
        setComic(res.comic)
        if (then) {
          leaving.current = true
          then()
          return true
        }
        if (next === 'published' && status === 'draft') {
          setPublished(res.comic)
        } else if (!comic) {
          // A new draft becomes an edit of that draft
          leaving.current = true
          navigate(`/artist/edit/${res.comic.id}`, { replace: true, state: { message: 'Draft saved.' } })
        } else {
          setMessage(next === 'draft' && status === 'published' ? 'Comic unpublished. It is now a draft.' : next === 'draft' ? 'Draft saved.' : 'Changes saved.')
        }
        return true
      } catch (error) {
        if (error instanceof ApiError && Object.keys(error.fields).length) {
          setProblems({ summary: error.message, fields: error.fields })
        } else {
          setSaveFailed({ title: "Changes couldn't be saved", retry: () => save(next, then) })
        }
        window.scrollTo({ top: 0, behavior: 'smooth' })
        return false
      } finally {
        setBusy(null)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [values, snapshot, comic, status]
  )

  const remove = async () => {
    if (!comic) return
    setBusy('deleting')
    try {
      await api.del(`/api/admin/comics/${comic.id}`)
      leaving.current = true
      navigate('/artist/comics', { state: { deleted: comic.title || '[Untitled comic]' } })
    } catch {
      setConfirmDelete(false)
      setSaveFailed({ title: "The comic couldn't be deleted", retry: () => setConfirmDelete(true) })
      setBusy(null)
    }
  }

  if (published) {
    return (
      <PublishedScreen
        comic={published}
        onAnother={() => {
          leaving.current = true
          navigate('/artist/upload', { replace: true, state: { fresh: Date.now() } })
        }}
      />
    )
  }

  const locked = Boolean(busy)
  const waitUploads = uploads.busy
  const disabled = locked || waitUploads
  const primaryLabel = status === 'published' ? 'Save changes' : 'Publish'
  const previewComic = {
    id: comic?.id ?? 'preview',
    title: values.title || '[Untitled comic]',
    slug: comic?.slug ?? '',
    caption: values.caption,
    tags,
    date: date || today(),
    displayStyle,
    images: uploads.saved,
    thumbnailIndex: thumbIndex,
    links: comic?.links,
  }

  return (
    <div className="stack lg">
      <header className="vstack gap-sm">
        <div className="eyebrow">Artist tools</div>
        <h1 className="h1">{editing ? 'Edit comic' : 'Upload a new comic'}</h1>
        {comic && (
          <div className="hstack gap-sm">
            <span className={`pill ${comic.status}`}>{comic.status === 'published' ? 'Published' : 'Draft'}</span>
            {comic.status === 'published' && (
              <Link to={comicPath(comic.slug)} className="strong">
                View comic page
              </Link>
            )}
          </div>
        )}
      </header>

      {message && (
        <Alert tone="ok" title={message}>
          {comic?.status === 'published' ? <Link to={comicPath(comic.slug)}>View comic page</Link> : null}
        </Alert>
      )}
      {problems && <Alert title={problems.summary}>{Object.values(problems.fields).join(' ')}</Alert>}
      {saveFailed && (
        <Alert
          title={saveFailed.title}
          actions={
            <button type="button" className="btn sm" onClick={saveFailed.retry}>
              Try again
            </button>
          }
        >
          Your edits are still here. Check your connection and try again.
        </Alert>
      )}
      {busy === 'publishing' && (
        <Alert tone="info" title="Publishing your comic">
          This takes a few seconds. Please keep this page open.
        </Alert>
      )}

      <section aria-labelledby="img-title" className="vstack gap-lg">
        <h2 id="img-title" className="h2">
          Images
        </h2>
        <ImageList uploads={uploads} disabled={locked} thumbnail={{ id: thumbId, set: setThumbId }} error={problems?.fields.images} idPrefix="comic" />
        {waitUploads && <div className="hint">Publishing is available once all uploads finish.</div>}
      </section>

      <section aria-labelledby="det-title" className="vstack gap-xl">
        <h2 id="det-title" className="h2">
          Details
        </h2>
        <div className="formfield">
          <label htmlFor="f-title" className="lbl">
            Comic title <span className="req">(required)</span>
          </label>
          <input
            id="f-title"
            className={problems?.fields.title ? 'field err' : 'field'}
            value={title}
            disabled={locked}
            aria-required="true"
            aria-invalid={Boolean(problems?.fields.title)}
            aria-describedby={problems?.fields.title ? 'f-title-err' : undefined}
            onChange={(e) => setTitle(e.target.value)}
          />
          {problems?.fields.title && (
            <div id="f-title-err" className="ferr" role="alert">
              <Icon name="alert" size={16} />
              {problems.fields.title}
            </div>
          )}
        </div>
        <CaptionField value={caption} onChange={setCaption} disabled={locked} />
        <TagField tags={tags} onChange={setTags} disabled={locked} />
        <div className="formfield narrow">
          <label htmlFor="f-date" className="lbl">
            Happened on <span className="hint">(optional)</span>
          </label>
          <input id="f-date" className="field" type="date" value={date} disabled={locked} onChange={(e) => setDate(e.target.value)} />
          <div className="hint">Shown to readers and used to order comics. Defaults to today.</div>
        </div>
        <DisplayStyleField value={displayStyle} onChange={setDisplayStyle} disabled={locked} />
      </section>

      <div className="btns bar">
        <button type="button" className="btn" disabled={disabled} onClick={() => setPreview(true)}>
          <Icon name="eye" size={18} />
          Preview
        </button>
        {status === 'published' ? (
          <button type="button" className="btn" disabled={disabled} onClick={() => save('draft')}>
            Unpublish
          </button>
        ) : (
          <button type="button" className="btn" disabled={disabled} onClick={() => save('draft')}>
            {busy === 'saving' && <span className="spin" aria-hidden="true" />}
            Save draft
          </button>
        )}
        <button type="button" className="btn solid" disabled={disabled} onClick={() => save('published')}>
          {(busy === 'publishing' || (busy === 'saving' && status === 'published')) && <span className="spin" aria-hidden="true" />}
          {busy === 'publishing' ? 'Publishing' : primaryLabel}
        </button>
      </div>

      {comic && (
        <section aria-labelledby="del-title" className="danger-zone">
          <div className="grow vstack gap-xs">
            <h2 id="del-title" className="h5">
              Delete this comic
            </h2>
            <div className="hint">Removes the comic and its images from the site. This can't be undone.</div>
          </div>
          <button type="button" className="btn danger" disabled={locked} onClick={() => setConfirmDelete(true)}>
            Delete comic
          </button>
        </section>
      )}

      {preview && (
        <Dialog labelledBy="pv-title" onClose={() => setPreview(false)} className="dialog preview" center={false}>
          <div className="dialog-head">
            <div>
              <h2 id="pv-title">Preview</h2>
              <div className="hint">This is how visitors will see the comic.</div>
            </div>
            <button type="button" className="iconbtn" aria-label="Close preview" onClick={() => setPreview(false)}>
              <Icon name="close" />
            </button>
          </div>
          <div className="dialog-body">
            <h1 className="h1 ink mb-sm">
              {previewComic.title}
            </h1>
            <MetaLine date={previewComic.date} tags={tags} />
            {uploads.saved.length ? <ComicImages comic={previewComic} where="page" /> : <p className="hint">No images yet.</p>}
            <Caption caption={previewComic.caption} links={previewComic.links} />
          </div>
          <div className="dialog-foot">
            <button type="button" className="btn" onClick={() => setPreview(false)}>
              Back to editing
            </button>
            <button type="button" className="btn solid" onClick={() => save('published')}>
              {primaryLabel}
            </button>
          </div>
        </Dialog>
      )}

      {confirmDelete && (
        <Dialog labelledBy="cd-title" alert onClose={() => setConfirmDelete(false)}>
          <h2 id="cd-title">Delete this comic?</h2>
          <p>This removes the comic and its images from the site. This can't be undone.</p>
          <div className="btns end mt-sm">
            <button type="button" className="btn" onClick={() => setConfirmDelete(false)} disabled={busy === 'deleting'}>
              Cancel
            </button>
            <button type="button" className="btn danger solid" onClick={remove} disabled={busy === 'deleting'}>
              {busy === 'deleting' && <span className="spin" aria-hidden="true" />}
              Delete comic
            </button>
          </div>
        </Dialog>
      )}

      {blocker.state === 'blocked' && (
        <Dialog labelledBy="lv-title" alert onClose={() => blocker.reset()}>
          <h2 id="lv-title">Leave without saving?</h2>
          <p>
            {status === 'published'
              ? "You have changes that haven't been saved. Save them, or leave and lose them."
              : "You have changes that haven't been saved. Save them as a draft to pick up later, or leave and lose them."}
          </p>
          <div className="btns end mt-sm">
            <button type="button" className="btn" onClick={() => blocker.reset()}>
              Keep editing
            </button>
            <button
              type="button"
              className="btn"
              disabled={uploads.busy}
              onClick={async () => {
                const ok = await save(status === 'published' ? 'published' : 'draft', () => blocker.proceed())
                if (!ok) blocker.reset()
              }}
            >
              {status === 'published' ? 'Save changes' : 'Save draft'}
            </button>
            <button type="button" className="btn danger solid" onClick={() => blocker.proceed()}>
              Leave
            </button>
          </div>
        </Dialog>
      )}
    </div>
  )
}
